// BrancheZweiSelect.tsx — Branchen-Klassifikation auf zwei Ebenen.
//
// Ein Block = EINE Branche (Ebene 1) + beliebig viele Branchengruppen
// (Ebene 2). „+ weitere Branche" hängt einen Block an; die erste Gruppe des
// ersten Blocks ist die Hauptbranche.
//
// SEIT 09.09.2026 (PO) stehen Branche und Branchengruppe als ZWEI DROPDOWNS
// NEBENeinander, beide mit dem Platzhalter „keine ausgewählt", darunter
// „+ weitere Branche". Das löst die vorherige Bauform ab (Branche als natives
// <select>, Gruppen als sichtbare Hakenliste darunter): Ein natives <select>
// kann in seinen Zeilen kein Bild tragen, und die Branchen-Icons (Anforderung
// 6) gehören sowohl vor jeden Eintrag als auch vor den gewählten Wert.
//
// SEIT 28.09.2026 (User) gibt es in der Oberfläche KEINE Hauptbranche mehr:
// alle Zeilen sind gleichrangig, Abzeichen und „Als Hauptbranche" entfallen
// (nur noch mit `mitHauptbranche`). Gespeichert wird weiterhin jede Zeile in
// ContactBranchengruppeZuordnung; die erste spiegelt der Aufrufer wie bisher
// in die Kontaktspalten (branchengruppeId/brancheFreitext/jobIndustry), weil
// Listen, Suche und Filter daraus lesen.
//
// Die Reihenfolge der Auswahl bleibt trotzdem erhalten: Die erste Zeile
// steht zusätzlich am Kontakt. Alphabetisch sortiert
// (und die Auffangeinträge ans Ende gepinnt) werden deshalb ausschließlich die
// OPTIONSLISTEN — `sortierteEbene1`/`sortierteGruppen`/`angeboteneEbene1` in
// `data/brancheGruppen.ts`. Die Auswahl selbst bleibt in Klick-Reihenfolge.
//
// „Sonstiges" ist eine ECHTE Katalogzeile mit dem Flag `erlaubtFreitext`, kein
// Sentinel: Nur so trägt die Auswahl weiterhin eine echte id, und die drei
// Stellen, die eine Auswahl ohne positive id verwerfen, bleiben unangetastet.

import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { SearchableSelect, type SelectOption } from "./SearchableSelect";
import { MehrfachSelect } from "./MehrfachSelect";
import {
  angeboteneEbene1,
  findGruppe,
  formatGruppeMitFreitext,
  istDeaktiviert,
  sortierteGruppen,
  useBrancheGruppen,
  type BrancheGruppe,
} from "../../../data/brancheGruppen";
import { brancheFluentIcon } from "../../../data/brancheFluentIcons";
import {
  alsHauptbrancheSetzen,
  auswahlAusBloecken,
  bloeckeAusAuswahl,
  freitextAufraeumen,
  freitextSetzen,
  gruppenFeldBeschriftung,
  gruppeVorwaehlen,
  idsOrdnen,
  idsUmschalten,
  type BrancheGruppenBlock,
  type BrancheGruppenWahl,
} from "./brancheGruppenLogik";
import { bestaetigen } from "../../redesign/bestaetigen";
import {
  BRANCHE_BEGRIFFE_FIRMA,
  type BrancheBegriffe,
} from "../../../utils/koerperschaftBegriff";

/** Ab wie vielen Gruppen ein Suchfeld über der Liste erscheint. */
const SUCHE_AB = 10;

const useStyles = makeStyles({
  block: { display: "flex", flexDirection: "column", rowGap: "12px" },
  bloecke: { display: "flex", flexDirection: "column", rowGap: "16px" },
  brancheBlock: { display: "flex", flexDirection: "column", rowGap: "10px" },
  brancheBlockWeiter: {
    paddingTop: "16px",
    ...shorthands.borderTop("1px", "dashed", REFINED.line),
  },
  blockLeiste: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    columnGap: "10px",
  },
  blockTitel: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.72rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: REFINED.ink3,
  },
  // Branche und Branchengruppe stehen NEBENeinander (PO 09.09.2026).
  //
  // `auto-fit`/`minmax` misst den CONTAINER, nicht das Fenster: Das Widget
  // hängt in drei verschieden breiten Masken (Anlage = volle Kartenbreite,
  // Kontaktakte = `.fld.span2` im zweispaltigen `.fgrid`, Beziehungs-Popup =
  // rund 320 px). Eine Media Query (formPrimitives bricht bei 600 px, das
  // `.fgrid` bei 768 px) sieht nur die Fensterbreite und ließe im schmalen
  // Panel eines breiten Edge-Fensters zwei unlesbar schmale Felder stehen.
  kopf: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    columnGap: "12px",
    rowGap: "12px",
    alignItems: "start",
  },
  label: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: REFINED.ink2,
    letterSpacing: "0.02em",
  },
  feld: { display: "flex", flexDirection: "column", rowGap: "6px" },
  ikon: { display: "inline-flex", alignItems: "center", color: REFINED.aub, flexShrink: 0 },
  freitext: {
    width: "100%",
    boxSizing: "border-box",
    ...shorthands.padding("7px", "10px"),
    ...shorthands.border("1px", "solid", REFINED.aub100),
    ...shorthands.borderRadius("7px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.86rem",
  },
  hinweis: { color: REFINED.ink3, fontSize: "0.78rem" },
  unfertig: { color: REFINED.aub, fontSize: "0.78rem" },
  alt: { color: REFINED.ink3, fontSize: "0.76rem", marginLeft: "6px" },
  zeilen: { display: "flex", flexDirection: "column", rowGap: "8px" },
  zeile: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    ...shorthands.padding("8px", "10px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
  },
  zeileKopf: { display: "flex", alignItems: "center", columnGap: "8px", flexWrap: "wrap" },
  pfad: { fontFamily: REFINED.fontUI, fontSize: "0.86rem", color: REFINED.ink, fontWeight: 600 },
  badge: {
    ...shorthands.padding("2px", "7px"),
    ...shorthands.borderRadius("999px"),
    backgroundColor: REFINED.aub50,
    color: REFINED.aub,
    fontSize: "0.72rem",
    fontWeight: 700,
  },
  linkBtn: {
    appearance: "none",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    ...shorthands.padding("0"),
    ":hover": { textDecorationLine: "underline" },
  },
});

/** Das Bildzeichen einer Branche im Fluss der Feldzeile. */
const BrancheIkon: React.FC<{ ebene1: string; className: string }> = ({ ebene1, className }) => {
  const Icon = brancheFluentIcon(ebene1);
  return (
    <span className={className}>
      <Icon aria-hidden />
    </span>
  );
};

/** EIN Block: Branche + Branchengruppen + Freitext + die gewählten Zeilen. */
const BrancheBlockFeld: React.FC<{
  idBase: string;
  index: number;
  block: BrancheGruppenBlock;
  rows: BrancheGruppe[];
  auswahl: BrancheGruppenWahl[];
  /** Die anzubietenden Branchen (bereits sortiert und gefiltert). */
  e1Namen: string[];
  loading: boolean;
  required?: boolean;
  istErster: boolean;
  entfernenLabel: string | null;
  onEbene1: (ebene1: string) => void;
  onIds: (ids: number[]) => void;
  onFreitext: (id: number, text: string) => void;
  onHauptbranche: (id: number) => void;
  onEntfernen: () => void;
  mitHauptbranche: boolean;
  begriffe: BrancheBegriffe;
}> = ({
  idBase,
  index,
  block,
  rows,
  auswahl,
  e1Namen,
  loading,
  required,
  istErster,
  entfernenLabel,
  onEbene1,
  onIds,
  onFreitext,
  onHauptbranche,
  onEntfernen,
  mitHauptbranche,
  begriffe,
}) => {
  const s = useStyles();

  const gruppen = React.useMemo(
    () => (block.ebene1 ? sortierteGruppen(rows, block.ebene1, block.ids) : []),
    [rows, block.ebene1, block.ids],
  );

  const e1Optionen: SelectOption[] = React.useMemo(() => {
    const liste: SelectOption[] = e1Namen.map((e1) => ({
      value: e1,
      label: e1,
      icon: <BrancheIkon ebene1={e1} className={s.ikon} />,
    }));
    // Ohne diese Zeile ließe sich eine versehentlich gewählte Branche nicht
    // mehr los: Das Feld nimmt keinen Freitext an (`allowFreeText={false}`),
    // und den Link „Branche zurücksetzen" gibt es in der kompakten Variante
    // nicht.
    //
    // Sie trägt ein eigenes, neutrales Zeichen: Ohne Icon begänne ihr Text rund
    // 30 px weiter links als alle anderen Zeilen und läse sich wie ein normaler
    // Katalogeintrag statt wie eine Aktion.
    return block.ebene1
      ? [
          {
            value: "",
            label: "keine ausgewählt",
            icon: (
              <span className={s.ikon}>
                <Dismiss20Regular aria-hidden />
              </span>
            ),
          },
          ...liste,
        ]
      : liste;
  }, [e1Namen, block.ebene1, s.ikon]);

  const gruppenOptionen = React.useMemo(
    () =>
      gruppen.map((g) => ({
        value: g.id,
        label: g.ebene2,
        hint: istDeaktiviert(g) ? "(deaktiviert)" : undefined,
      })),
    [gruppen],
  );

  const zeilen = block.ids.map((id) => ({
    id,
    gruppe: findGruppe(rows, id),
    wahl: auswahl.find((a) => a.branchengruppeId === id),
  }));

  // Der Freitext hängt am Datenmodell an der ZUORDNUNG, nicht an einer Ebene.
  // Je Branche gibt es genau eine Gruppe mit `erlaubtFreitext` — deshalb steht
  // hier höchstens EIN Feld, gleich ob der Anwender die Auffang-Branche oder
  // die Auffang-Gruppe gewählt hat. Zwei Eingabefelder auf denselben
  // Speicherplatz wären ein stiller Datenverlust.
  const freitextZeile = zeilen.find((z) => z.gruppe?.erlaubtFreitext === true);

  return (
    <div className={mergeClasses(s.brancheBlock, index > 0 && s.brancheBlockWeiter)}>
      {entfernenLabel && (
        <div className={s.blockLeiste}>
          <span className={s.blockTitel}>{block.ebene1 || begriffe.weitererBlockTitel}</span>
          <button type="button" className={s.linkBtn} onClick={onEntfernen}>
            {entfernenLabel}
          </button>
        </div>
      )}

      <div className={s.kopf}>
        <SearchableSelect
          label={begriffe.ebene1Label}
          name={`${idBase}-ebene1-${index}`}
          value={block.ebene1}
          onChange={onEbene1}
          options={e1Optionen}
          // Ein getippter Branchenname ist KEINE Branche: Ohne Katalogzeile
          // gibt es keine Gruppe und damit nichts zu speichern.
          allowFreeText={false}
          leftIcon={
            block.ebene1 ? <BrancheIkon ebene1={block.ebene1} className={s.ikon} /> : undefined
          }
          placeholder={loading ? "Lädt…" : "keine ausgewählt"}
          emptyText={begriffe.ebene1NichtGefunden}
          required={required}
        />

        <MehrfachSelect
          label={begriffe.ebene2Label}
          name={`${idBase}-gruppen-${index}`}
          value={block.ids}
          options={gruppenOptionen}
          onToggle={(id) => onIds(idsUmschalten(block.ids, id))}
          beschriftung={
            loading ? "Lädt…" : gruppenFeldBeschriftung(rows, block.ids)
          }
          // Ohne Branche gibt es nichts zu wählen — ein Feld, das sich öffnen
          // lässt und dann leer ist, sieht aus wie ein Fehler.
          disabled={loading || !block.ebene1}
          hinweis={!loading && !block.ebene1 ? begriffe.zuerstEbene1 : undefined}
          sucheAb={SUCHE_AB}
          sucheLabel={begriffe.ebene2Suche}
          emptyText={begriffe.ebene2NichtGefunden}
        />
      </div>

      {/* Eine Branche ohne Gruppe lässt sich nicht speichern; sie fiele beim
          Neuladen weg. Damit das nicht wie Datenverlust aussieht, sagt der
          Block es selbst. */}
      {block.ids.length === 0 && block.ebene1 !== "" && (
        <div className={s.unfertig}>{begriffe.ohneEbene2}</div>
      )}

      {/* Das Freitextfeld steht UNTER den Dropdowns (PO 09.09.2026) und ist
          bewusst keine Pflicht: Ein Speicherstopp wegen einer Katalogfeinheit
          wäre in dieser Maske nicht erklärbar. Der Hinweis darunter sagt, was
          fehlt. */}
      {freitextZeile?.gruppe && (
        <div className={s.feld}>
          <label className={s.label} htmlFor={`${idBase}-freitext-${index}`}>
            {`Bitte beschreiben: ${freitextZeile.gruppe.ebene1} / ${freitextZeile.gruppe.ebene2}`}
          </label>
          <input
            id={`${idBase}-freitext-${index}`}
            className={s.freitext}
            value={freitextZeile.wahl?.freitext ?? ""}
            placeholder="z. B. Bühnen- und Messebau"
            onChange={(e) => onFreitext(freitextZeile.id, e.target.value)}
          />
          {!(freitextZeile.wahl?.freitext ?? "").trim() && (
            <span className={s.hinweis}>Ohne Beschreibung steht in der Akte nur „Sonstiges".</span>
          )}
        </div>
      )}

      {zeilen.length > 0 && (
        <div className={s.zeilen}>
          {zeilen.map(({ id, gruppe, wahl }, i) => (
            <div key={id} className={s.zeile}>
              <div className={s.zeileKopf}>
                <span className={s.pfad}>
                  {formatGruppeMitFreitext(gruppe, wahl?.freitext) || `Branche #${id}`}
                </span>
                {istDeaktiviert(gruppe) && <span className={s.alt}>(deaktiviert)</span>}
                {/* Die einzige Stelle, an der die REIHENFOLGE sichtbar und
                    änderbar ist — sie entscheidet, welche Zeile als
                    Hauptbranche zusätzlich am Kontakt steht. Die
                    Feldbeschriftung „n ausgewählt" kann das nicht zeigen. */}
                {!mitHauptbranche ? null : istErster && i === 0 ? (
                  <span className={s.badge}>Hauptbranche</span>
                ) : (
                  <button type="button" className={s.linkBtn} onClick={() => onHauptbranche(id)}>
                    Als Hauptbranche
                  </button>
                )}
                <button
                  type="button"
                  className={s.linkBtn}
                  onClick={() => onIds(idsUmschalten(block.ids, id))}
                >
                  Entfernen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const BrancheZweiSelect: React.FC<{
  value: BrancheGruppenWahl[];
  onChange: (next: BrancheGruppenWahl[]) => void;
  /** Nur-Lese-Ansicht (fehlendes Schreibrecht). */
  editable?: boolean;
  /** Branchenfeld des ersten Blocks als Pflichtfeld markieren. */
  required?: boolean;
  /** Präfix für die DOM-Ids — mehrere Instanzen je Seite möglich. */
  idBase?: string;
  /** Sparsame Variante fürs Firma-Popup: keine Block-Kopfzeile beim einzigen Block. */
  kompakt?: boolean;
  /**
   * Hauptbranche wieder zeigen (Abzeichen + „Als Hauptbranche"). Seit
   * 28.09.2026 aus — alle Branchen sind gleichrangig (User).
   */
  mitHauptbranche?: boolean;
  /**
   * Beschriftungen — beim Verein „Ausrichtung und Schwerpunkt" statt
   * „Branche" (`KoerperschaftTexte.branche`). Ohne Angabe die Branchen-Texte.
   */
  begriffe?: BrancheBegriffe;
}> = ({
  value,
  onChange,
  editable = true,
  required,
  idBase = "branchengruppe",
  kompakt = false,
  mitHauptbranche = false,
  begriffe = BRANCHE_BEGRIFFE_FIRMA,
}) => {
  const s = useStyles();
  const { rows, loading } = useBrancheGruppen();

  // Die Blockliste ist der lokale Bearbeitungsstand: Ein Block mit gewählter
  // Branche, aber noch ohne Gruppe, ist nicht speicherbar und käme über `value`
  // nie zurück — er muss hier leben.
  const [bloecke, setBloecke] = React.useState<BrancheGruppenBlock[]>(() =>
    bloeckeAusAuswahl(rows, value),
  );
  // Signatur der zuletzt selbst gemeldeten Auswahl: Nur eine Änderung VON AUSSEN
  // (Laden, Zurücksetzen, Speichern) baut die Blöcke neu auf. Sonst sammelte
  // jeder eigene onChange die leeren Blöcke gleich wieder ein.
  const gemeldet = React.useRef<string | null>(null);
  const eingang = JSON.stringify(value.map((a) => a.branchengruppeId));

  React.useEffect(() => {
    // Ohne Katalog ist die Branche einer id unbekannt — dann lieber warten,
    // statt alles in einen Block ohne Branche zu werfen.
    if (loading || gemeldet.current === eingang) return;
    gemeldet.current = eingang;
    setBloecke(bloeckeAusAuswahl(rows, value));
  }, [loading, rows, eingang, value]);

  const gewaehlteIds = React.useMemo(() => bloecke.flatMap((b) => b.ids), [bloecke]);
  // Alphabetisch, Auffangbranchen zuletzt, und die künstliche „Sonstiges"-
  // Branche nur noch, wenn dieser Kontakt sie bereits trägt.
  const alleE1 = React.useMemo(() => angeboteneEbene1(rows, gewaehlteIds), [rows, gewaehlteIds]);

  const melden = (next: BrancheGruppenBlock[]) => {
    const auswahl = freitextAufraeumen(rows, auswahlAusBloecken(next, value));
    gemeldet.current = JSON.stringify(auswahl.map((a) => a.branchengruppeId));
    setBloecke(next);
    onChange(auswahl);
  };

  const ersetzen = (i: number, b: BrancheGruppenBlock) =>
    melden(bloecke.map((alt, j) => (j === i ? b : alt)));

  const ebene1Setzen = async (i: number, ebene1: string) => {
    const alt = bloecke[i];
    if (alt.ebene1 === ebene1) return;
    // Eine Branche mit genau einer Gruppe und die Auffangbranche setzen ihre
    // Gruppe selbst — bei der Auffangbranche hängt daran das Freitextfeld.
    const automatisch = gruppeVorwaehlen(rows, ebene1);
    const ids = automatisch != null ? [automatisch] : [];
    if (alt.ids.length === 0) {
      if (ids.length === 0) {
        // Reiner Maskenzustand — die gespeicherte Auswahl ändert sich nicht.
        setBloecke(bloecke.map((b, j) => (j === i ? { ebene1, ids: [] } : b)));
        return;
      }
      ersetzen(i, { ebene1, ids });
      return;
    }
    const ok = await bestaetigen({
      titel: begriffe.wechselTitel,
      text: begriffe.wechselText,
      ja: begriffe.wechselJa,
      ton: "danger",
    });
    if (!ok) return;
    ersetzen(i, { ebene1, ids });
  };

  // `idsOrdnen` hält die Auffanggruppe „Sonstiges" hinten: Sie wird beim Wechsel
  // auf die Auffangbranche automatisch angehakt (nur an ihr kann der Freitext
  // hängen) und dürfte deshalb nicht die HAUPTbranche der ganzen Firma werden,
  // sobald der Anwender eine benannte Gruppe wie „Sport" ankreuzt.
  const idsSetzen = (i: number, ids: number[]) =>
    ersetzen(i, { ...bloecke[i], ids: idsOrdnen(rows, ids) });

  const freitextAendern = (id: number, text: string) => {
    const auswahl = auswahlAusBloecken(bloecke, value);
    onChange(freitextSetzen(auswahl, id, text));
  };

  const blockEntfernen = (i: number) => {
    const rest = bloecke.filter((_, j) => j !== i);
    melden(rest.length > 0 ? rest : [{ ebene1: "", ids: [] }]);
  };

  const blockHinzufuegen = () => setBloecke([...bloecke, { ebene1: "", ids: [] }]);

  // Jede Branche gehört genau einem Block. Sind alle vergeben, hätte ein
  // weiterer Block nichts mehr zu wählen — eine Sackgasse, also gar nicht erst
  // anbieten.
  const keineFreieBranche =
    alleE1.length > 0 && alleE1.every((e1) => bloecke.some((b) => b.ebene1 === e1));

  const blockAktion = (b: BrancheGruppenBlock): string | null => {
    if (bloecke.length > 1) return begriffe.blockEntfernen;
    if (kompakt) return null;
    return b.ebene1 || b.ids.length > 0 ? begriffe.blockZuruecksetzen : null;
  };

  if (!editable) {
    const gruppen = bloeckeAusAuswahl(rows, value).filter((b) => b.ids.length > 0);
    return (
      <div className={s.block}>
        <span className={s.label}>{begriffe.leseLabel}</span>
        {gruppen.length === 0 ? (
          <span className={s.hinweis}>{begriffe.leer}</span>
        ) : (
          <div className={s.bloecke}>
            {gruppen.map((b, bi) => (
              <div
                key={b.ebene1 || `ohne-${bi}`}
                className={mergeClasses(s.brancheBlock, bi > 0 && s.brancheBlockWeiter)}
              >
                {/* Dasselbe Zeichen wie im Dropdown — sonst trüge die Branche
                    in der Bearbeitung ein anderes Bild als in der Ansicht. */}
                <span className={s.blockTitel}>
                  {b.ebene1 && <BrancheIkon ebene1={b.ebene1} className={s.ikon} />}
                  {b.ebene1 || "Ohne Branche"}
                </span>
                <div className={s.zeilen}>
                  {b.ids.map((id, i) => (
                    <div key={id} className={s.zeile}>
                      <div className={s.zeileKopf}>
                        <span className={s.pfad}>
                          {formatGruppeMitFreitext(
                            findGruppe(rows, id),
                            value.find((a) => a.branchengruppeId === id)?.freitext,
                          ) || `Branche #${id}`}
                        </span>
                        {istDeaktiviert(findGruppe(rows, id)) && (
                          <span className={s.alt}>(deaktiviert)</span>
                        )}
                        {mitHauptbranche && bi === 0 && i === 0 && (
                          <span className={s.badge}>Hauptbranche</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={s.block}>
      <div className={s.bloecke}>
        {bloecke.map((b, i) => {
          const belegt = new Set(
            bloecke.filter((_, j) => j !== i).map((x) => x.ebene1).filter(Boolean),
          );
          return (
            <BrancheBlockFeld
              // Position statt Branche als Schlüssel: Ein Schlüsselwechsel baute
              // den Block bei der ersten Auswahl neu auf und setzte das
              // Suchfeld zurück.
              key={i}
              idBase={idBase}
              index={i}
              block={b}
              rows={rows}
              auswahl={value}
              e1Namen={alleE1.filter((e1) => !belegt.has(e1))}
              loading={loading}
              required={i === 0 ? required : undefined}
              istErster={i === 0}
              entfernenLabel={blockAktion(b)}
              onEbene1={(e1) => ebene1Setzen(i, e1)}
              onIds={(ids) => idsSetzen(i, ids)}
              onFreitext={freitextAendern}
              onHauptbranche={(id) => {
                const auswahl = alsHauptbrancheSetzen(auswahlAusBloecken(bloecke, value), id);
                gemeldet.current = JSON.stringify(auswahl.map((a) => a.branchengruppeId));
                setBloecke(bloeckeAusAuswahl(rows, auswahl));
                onChange(auswahl);
              }}
              onEntfernen={() => blockEntfernen(i)}
              mitHauptbranche={mitHauptbranche}
              begriffe={begriffe}
            />
          );
        })}
      </div>
      {!keineFreieBranche && (
        <button type="button" className={s.linkBtn} onClick={blockHinzufuegen}>
          {begriffe.weitereHinzufuegen}
        </button>
      )}
    </div>
  );
};

export default BrancheZweiSelect;
